import React, { useEffect, useMemo, useState } from "react";
import { makeKeyInLowerCaseHelper } from "../../../utils";
import {
	authAxios,
	fetchWithAuthorization,
	getEmailFromSessionStorage,
} from "../../../utils/session_utils"; // Updated import
import { PaginationProps } from "antd/lib/pagination";
import { Form, message, Switch, TablePaginationConfig } from "antd";
import { DealManagerView, getDealManagerConfig } from "./utils";
import { CustomBtn } from "../../../series/StyledComponent";
import { EditOutlined } from "@ant-design/icons";
import { isAxiosError } from "axios";

const controller = new AbortController();
export const useDealManagerData = () => {
	const [view, setView] = useState<DealManagerView>("deals");
	const [form] = Form.useForm();
	const [dealData, setDealData] = useState<any[]>([]);
	const [dealGridData, setDealGridData] = useState<any[]>([]);
	const [channelData, setChannelData] = useState<any[]>([]);
	const [territoryData, setTerritoryData] = useState<any[]>([]);
	const [partnerData, setPartnerData] = useState<any[]>([]);
	const [platformData, setPlatformData] = useState<any[]>([]);

	const [editId, setEditId] = useState<number>();
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [pagination, setPagination] = useState<PaginationProps>({
		current: 1,
		pageSize: 10,
		hideOnSinglePage: false,
		showSizeChanger: true,
	});

	const activeColumn = (
		// table: string,
		onChangeHandler: (record: any) => void,
		title = "Active",
		dataIndex = "ACTIVE"
	) => {
		return {
			title,
			dataIndex,
			key: dataIndex,
			render: (text: boolean, record: any) => {
				return (
					<span onClick={e => e.stopPropagation()}>
						<Switch
							checked={text}
							onChange={() => onChangeHandler(record)}
							checkedChildren="Yes"
							unCheckedChildren="No"
						/>
					</span>
				);
			},
		};
	};

	const openEditForm = (record: any) => {
		if (["channel", "territory", "partner", "platform"].includes(view)) {
			setEditId(record.id);
		} else {
			setEditId(record.db_id ?? record.id);
		}
		const viewershipTypeArray = record?.viewership_type?.trim() ? record?.viewership_type?.trim()?.split(" + ") : [];
		if (view === "deal-grid") {
			form.setFieldsValue({
				deal_parent: record.deal_parent,
				platform: record.platform,
				partner: record.partner,
				channel_id: record.channel_id,
				territory_id: record.territory_id,
				channel: `${record.channel_id}_${record.channel}`,
				territory: `${record.territory_id}_${record.territory}`,
			});
		} else if (view === "deals") {
			form.setFieldsValue({
				...record,
				viewership_type: viewershipTypeArray,
			});
		} else {
			form.setFieldsValue({ ...record });
		}
		setIsModalOpen(true);
	};

	const activeSwitchHandler = async (record: any) => {
		try {
			const keyName = ["channel", "territory", "partner", "platform"].includes(view) ? "id" : "db_id";
			const config = {
				method: "PUT",
				url: `${process.env.REACT_APP_BACK_END_API}/register-data/make-record-active-inactive`,
				headers: { "Content-Type": "application/json" },
				data: JSON.stringify({
					[keyName]: record[keyName],
					active: !record.active,
					view,
				}),
			}
			setIsLoading(true);
			const {data} = await authAxios(config);
			message.success(data?.message || "Record status updated successfully");
			const updatedRecord = { ...record, active: !record.active };
			if (apiPath === "/register-data/deal-grid") {
				setDealGridData(prevData => prevData.map(item => (item.db_id === record.db_id ? updatedRecord : item)));
			}
			if (apiPath === "/register-data/channels") {
				setChannelData(prevData => prevData.map(item => (item.id === record.id ? updatedRecord : item)));
			}
			if (apiPath === "/register-data/territories") {
				setTerritoryData(prevData => prevData.map(item => (item.id === record.id ? updatedRecord : item)));
			}
			if (apiPath === "/register-data/partners") {
				setPartnerData(prevData => prevData.map(item => (item.id === record.id ? updatedRecord : item)));
			}
			if (apiPath === "/register-data/platforms") {
				setPlatformData(prevData => prevData.map(item => (item.id === record.id ? updatedRecord : item)));
			}
		} catch (error) {
			console.error("Error updating active status:", error);
			message.error("Failed to update active status");
			return;
		} finally {
			setIsLoading(false);
		}
	};

	const deleteDealData = async (deleteKey: number) => {
		try {
			setIsLoading(true);
			setError(null);
			const keyName = ["channel", "territory", "partner", "platform"].includes(view) ? "id" : "db_id";
			const config = {
				method: "DELETE",
				url: `${process.env.REACT_APP_BACK_END_API}${apiPath}/${deleteKey}`,
				headers: { "Content-Type": "application/json" },
			};
			await authAxios(config);
		} catch (e: any) {
			setError("Failed to delete data");
		} finally {
			setIsLoading(false);
		}
	};

	const { apiPath, columns } = useMemo(() => {
		const { apiPath, columns } = getDealManagerConfig(view);
		// Add active switch column to the columns array
		if (view === "channel" || view === "territory" || view === "partner" || view === "platform") {
			columns.push(activeColumn(activeSwitchHandler, "Active", "active"));
		}
		// Add edit and delete actions to the columns
		columns.push({
			title: "Action",
			dataIndex: "action",
			key: "action",
			align: "right",
			render: (_: null, record: any) => {
				return (
					<>
						<CustomBtn
							onClick={e => {
								e.stopPropagation();
								openEditForm(record);
							}}
							icon={<EditOutlined />}
							type={"link"}
						/>
						{/* <DeletePopConfirm title="Delete ?" deleteHandler={() => deleteDealData(record.id)}>
							Are you sure you want to delete this record?
						</DeletePopConfirm> */}
					</>
				);
			},
		});
		return { apiPath, columns };
	}, [view]);

	// useEffect(() => {
	// 	getDataHandler();
	// 	// return () => controller.abort();
	// }, [apiPath]);

	useEffect(() => {
		getWholeData();
	}, [])

	const getWholeData = async () => {
		try {
			setIsLoading(true);
			setError(null);
			const [dealData, channelData, territoryData, partnerData, platformData, dealGridData] = await Promise.all([
				fetchWithAuthorization(`${process.env.REACT_APP_BACK_END_API}/register-data/deals`),
				fetchWithAuthorization(`${process.env.REACT_APP_BACK_END_API}/register-data/channels`),
				fetchWithAuthorization(`${process.env.REACT_APP_BACK_END_API}/register-data/territories`),
				fetchWithAuthorization(`${process.env.REACT_APP_BACK_END_API}/register-data/partners`),
				fetchWithAuthorization(`${process.env.REACT_APP_BACK_END_API}/register-data/platforms`),
				fetchWithAuthorization(`${process.env.REACT_APP_BACK_END_API}/register-data/deal-grid`),
			]);
			const [dealJson, channelJson, territoryJson, partnerJson, platformJson, dealGridJson] = await Promise.all([
				dealData.json(),
				channelData.json(),
				territoryData.json(),
				partnerData.json(),
				platformData.json(),
				dealGridData.json(),
			]);
			setDealData(makeKeyInLowerCaseHelper(dealJson?.data ?? []));
			setChannelData(makeKeyInLowerCaseHelper(channelJson?.data ?? []));
			setTerritoryData(makeKeyInLowerCaseHelper(territoryJson?.data ?? []));
			setPartnerData(makeKeyInLowerCaseHelper(partnerJson?.data ?? []));
			setPlatformData(makeKeyInLowerCaseHelper(platformJson?.data ?? []));
			setDealGridData(makeKeyInLowerCaseHelper(dealGridJson?.data ?? []));
		} catch (e: any) {
			if (isAxiosError(e)) {
				console.error("Error fetching whole data:", e?.response?.data);
			}	 else {
				console.error("Error fetching whole data:", e);
			}	
		} finally {
			setIsLoading(false);
		}
	};


	const getDataHandler = async () => {
		try {
			setIsLoading(true);
			setError(null);
			const url = `${process.env.REACT_APP_BACK_END_API}${apiPath}`;
			const res = await fetchWithAuthorization(url, { signal: controller.signal });
			const json = await res.json();
			const rows: any[] = json?.data ?? [];
			const lowerCaseData = await makeKeyInLowerCaseHelper(rows);
			if (apiPath === "/register-data/deal-grid") {
				setDealGridData(lowerCaseData);
			}
			if (apiPath === "/register-data/deals") {
				setDealData(lowerCaseData);
			}
			if (apiPath === "/register-data/channels") {
				setChannelData(lowerCaseData);
			}
			if (apiPath === "/register-data/territories") {
				setTerritoryData(lowerCaseData);
			}
			if (apiPath === "/register-data/partners") {	
				setPartnerData(lowerCaseData);
			}
			if (apiPath === "/register-data/platforms") {
				setPlatformData(lowerCaseData);
			}
		} catch (e: any) {
			if (e?.name !== "AbortError") {
				setError("Failed to fetch data");
			}
		} finally {
			setIsLoading(false);
		}
	};



	const handleTableChange = (newPagination: TablePaginationConfig, filters: any, sorter: any, extra: { currentDataSource: [], action: 'paginate' | 'sort' | 'filter' }) => {
		switch (extra.action) {
			case "paginate":
				if (newPagination.current !== pagination.current) {
					setPagination({
						...pagination,
						current: newPagination.current,
					});
				}
				break;
		}
	};

	const saveEditHandler = async () => {
		try {
			const newData = await form.validateFields();
			setIsFormLoading(true);
			setError(null);
			const keyName = ["channel", "territory", "partner", "platform"].includes(view) ? "id" : undefined;
			const config = {
				method: editId ? "PUT" : "POST",
				url: `${process.env.REACT_APP_BACK_END_API}${apiPath}`,
				headers: { "Content-Type": "application/json" },
				data: JSON.stringify({
					userEmail: getEmailFromSessionStorage() || "",
					...newData,
					...(keyName ? { [keyName]: editId } : {}),
					...(["deal-grid", "deals"].includes(view) ? (view === "deal-grid" || view === "deals" ? {id: editId} : {db_id: editId,}) : {})
				}),
			};
			await authAxios(config);
			await getDataHandler(); // Refresh data after save
			closeModalHandler();
		} catch (e: any) {
			setError("Failed to create data");
			if (isAxiosError(e)) {
				message.error(e.response?.data?.message ?? `Failed to ${editId ? "Edit" : "Create"} data`);
			}
		} finally {
			setIsFormLoading(false);
		}
	};

	const closeModalHandler = () => {
		form.resetFields();
		setEditId(undefined);
		setIsModalOpen(false);
	};

	const viewData = useMemo(() => {
		switch (view) {
			case "deal-grid":
				return dealGridData;
			case "channel":
				return channelData;
			case "territory":
				return territoryData;
			case "partner":
				return partnerData;
			case "platform":
				return platformData;
			case "deals":
				return dealData;
			default:
				return [];
		}
	}
	, [view, dealGridData, channelData, territoryData, partnerData, platformData, dealData]);

	return {
		view,
		setView,
		data: viewData,
		wholeData: {dealGridData, channelData, territoryData, partnerData, platformData, dealData},
		isLoading,
		error,
		columns,
		pagination,
		handleTableChange,
		saveEditHandler,
		editId,
		setEditId,
		isModalOpen,
		setIsModalOpen,
		form,
		closeModalHandler,
		openEditForm,
		isFormLoading, setIsFormLoading
	};
};

export default useDealManagerData;
